import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// frontend/src/components/SchedulesBoard/SchedulesBoard.tsx
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@weave-mui/material';
import { useSchedules, usePatchSchedule, useBatchDeleteSchedules } from '../../api/hooks';
import { getErrorDetail } from '../../utils/errorDetail';
import { useBoardSelection } from '../../hooks/useBoardSelection';
import BoardHeader from '../BoardHeader/BoardHeader';
import DeleteConfirmDialog from '../BoardHeader/DeleteConfirmDialog';
import CreateTaskModal from '../CreateTaskModal/CreateTaskModal';
import ScheduleCard from './ScheduleCard';
import ScheduleSidebar from './ScheduleSidebar';
const COLUMNS = [
    { status: 'ACTIVE', label: 'Active' },
    { status: 'PAUSED', label: 'Paused' },
    { status: 'CANCELLED', label: 'Cancelled' },
];
export default function SchedulesBoard() {
    const navigate = useNavigate();
    const { scheduleId } = useParams();
    const { data, isLoading, error, refetch, isRefetching, } = useSchedules();
    const { mutate: patchSchedule } = usePatchSchedule();
    const { mutateAsync: batchDeleteSchedules } = useBatchDeleteSchedules();
    const [createOpen, setCreateOpen] = useState(false);
    const { selectedIds, setSelectedIds, handleClick } = useBoardSelection();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    if (isLoading)
        return _jsx(CircularProgress, { sx: { m: 4 } });
    if (error) {
        return (_jsxs(Typography, { color: "error", sx: { m: 4 }, children: ["Failed to load schedules: ", getErrorDetail(error)] }));
    }
    const schedules = data?.results ?? [];
    const byStatus = (status) => schedules.filter((s) => s.scheduleStatus === status);
    function handleDrop(droppedScheduleId, newStatus) {
        patchSchedule({ scheduleId: droppedScheduleId, input: { scheduleStatus: newStatus } });
    }
    const selectedSchedule = scheduleId ? schedules.find((s) => s.scheduleId === scheduleId) : undefined;
    async function handleConfirmDelete() {
        setDeleting(true);
        setDeleteError(null);
        try {
            const outcome = await batchDeleteSchedules(Array.from(selectedIds));
            if (outcome.errors.length > 0) {
                setSelectedIds(new Set(outcome.errors.map((e) => e.id)));
                setDeleteError(`Failed to delete ${outcome.errors.length} of ${outcome.errors.length + outcome.results.length} schedule(s). They remain selected — try again.`);
            }
            else {
                setSelectedIds(new Set());
                setDeleteDialogOpen(false);
            }
        }
        catch (e) {
            setDeleteError(`Failed to delete schedules: ${getErrorDetail(e)}`);
        }
        finally {
            setDeleting(false);
        }
    }
    return (_jsxs(Box, { children: [_jsx(BoardHeader, { title: "Scheduled Tasks", menuActions: [
                    { label: 'New Schedule', onClick: () => setCreateOpen(true) },
                ], onRefresh: () => refetch(), refreshing: isRefetching, selectedCount: selectedIds.size, onDelete: () => { setDeleteError(null); setDeleteDialogOpen(true); } }), _jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'flex-start' }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', gap: 2 }, children: COLUMNS.map(({ status, label }) => (_jsxs(Box, { 
                            // minWidth: 0 overrides the flex item's default auto min-width (its content's
                            // max-content size) — without it, a long unwrapped title can blow the column
                            // past its equal share. Combined with the row's default align-items: stretch,
                            // this also equalizes column height regardless of card count.
                            sx: { flex: 1, minWidth: 0, bgcolor: '#f4f5f7', borderRadius: 2, p: 1, minHeight: 400 }, onDragOver: (e) => e.preventDefault(), onDrop: (e) => {
                                const droppedScheduleId = e.dataTransfer.getData('scheduleId');
                                if (droppedScheduleId)
                                    handleDrop(droppedScheduleId, status);
                            }, children: [_jsxs(Typography, { variant: "subtitle2", sx: { mb: 1, fontWeight: 700 }, children: [label, " (", byStatus(status).length, ")"] }), byStatus(status).map((schedule) => (_jsx(ScheduleCard, { schedule: schedule, selected: scheduleId === schedule.scheduleId, multiSelected: selectedIds.has(schedule.scheduleId), onClick: (s, e) => handleClick(s.scheduleId, status, byStatus(status).map((x) => x.scheduleId), e, () => navigate(`/schedules/${s.scheduleId}`)) }, schedule.scheduleId)))] }, status))) }), selectedSchedule && (_jsx(ScheduleSidebar, { schedule: selectedSchedule, onClose: () => navigate('/schedules') }, selectedSchedule.scheduleId))] }), _jsx(CreateTaskModal, { open: createOpen, onClose: () => setCreateOpen(false), mode: "schedule" }), _jsx(DeleteConfirmDialog, { open: deleteDialogOpen, count: selectedIds.size, itemLabel: "schedule", onCancel: () => { setDeleteDialogOpen(false); setDeleteError(null); }, onConfirm: handleConfirmDelete, deleting: deleting, error: deleteError })] }));
}
